import * as React from "react";
import { useHistory } from "react-router-dom";
import { PromptHistoryItem } from "../../../shared/apiService";
import { DEFAULT_PROMPT } from "../../../shared/constants";
import SpreetailHeader from "../layout/SpreetailHeader";

interface Props {
  editPromptValue: string;
  setEditPromptValue: (v: string) => void;
  savedPromptName: string;
  setSavedPromptName: (v: string) => void;
  savedPromptsRefresh: number;
  savedPromptTemplates: PromptHistoryItem[];
  promptHistory: PromptHistoryItem[];
  onCancel: () => void;
  onSavePrompt: (prompt: string, savedPromptName: string) => Promise<void>;
  onOpenInsights: (prompt: string, savedPromptName: string) => Promise<void>;
  loadPromptDataIfNeeded: () => Promise<void>; // NEW: Added prop
}

type TabType = "prompt" | "history";

export const EditPromptUI: React.FC<Props> = ({
  editPromptValue,
  setEditPromptValue,
  savedPromptName,
  setSavedPromptName,
  savedPromptTemplates,
  promptHistory,
  onSavePrompt,
  onOpenInsights,
  loadPromptDataIfNeeded, // NEW: Destructure the new prop
}) => {
  const [activeTab, setActiveTab] = React.useState<TabType>(() => {
    const savedTab = sessionStorage.getItem("activeTab") as TabType;
    return savedTab === "prompt" || savedTab === "history" ? savedTab : "prompt";
  });
  const [isSavedPromptSelected, setIsSavedPromptSelected] = React.useState(false);
  const [showNameValidationError, setShowNameValidationError] = React.useState(false);
  const [selectedPromptId, setSelectedPromptId] = React.useState<string | null>(null);
  const [saveStatus, setSaveStatus] = React.useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const history = useHistory();
  const isInitialRestore = React.useRef(true);
  const ITEMS_PER_PAGE = 10;

  // NEW: Load prompt data when component mounts (lazy loading)
  React.useEffect(() => {
    console.log("📂 EditPromptUI mounted - triggering data load...");
    loadPromptDataIfNeeded();
  }, [loadPromptDataIfNeeded]);

  React.useEffect(() => {
    sessionStorage.setItem("activeTab", activeTab);
  }, [activeTab]);

  const sortedTemplates = React.useMemo(() => {
    return [...savedPromptTemplates].sort((a, b) => b.createdAt - a.createdAt);
  }, [savedPromptTemplates]);

  const sortedHistory = React.useMemo(() => {
    return [...promptHistory].sort((a, b) => b.createdAt - a.createdAt);
  }, [promptHistory]);

  // Reset to page 1 when history changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [promptHistory.length]);

  // Calculate pagination values
  const totalPages = Math.ceil(sortedHistory.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const visibleHistory = React.useMemo(() => {
    return sortedHistory.slice(startIndex, endIndex);
  }, [sortedHistory, startIndex, endIndex]);

  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  React.useEffect(() => {
    isInitialRestore.current = true;

    if (sortedTemplates.length === 0) {
      return;
    }

    const savedPromptId = sessionStorage.getItem("selectedPromptId");

    if (savedPromptId) {
      const savedPrompt = sortedTemplates.find((p) => p.id === savedPromptId);
      if (savedPrompt) {
        const valuesMatch =
          editPromptValue === savedPrompt.prompt && savedPromptName === savedPrompt.shortName;
        const valuesEmpty = !editPromptValue && !savedPromptName;

        if (isInitialRestore.current && (valuesEmpty || valuesMatch)) {
          console.log(
            "🔄 UI: Restoring selected prompt from sessionStorage:",
            savedPrompt.shortName
          );
          setSelectedPromptId(savedPrompt.id);
          setIsSavedPromptSelected(true);

          if (valuesEmpty) {
            setEditPromptValue(savedPrompt.prompt);
            setSavedPromptName(savedPrompt.shortName);
          }
          isInitialRestore.current = false;
        } else if (!valuesMatch && !valuesEmpty) {
          // User has edited, clear the selection
          setSelectedPromptId(null);
          setIsSavedPromptSelected(false);
          sessionStorage.removeItem("selectedPromptId");
          isInitialRestore.current = false;
        }
        return;
      }
    }

    if (isInitialRestore.current && editPromptValue && savedPromptName && !selectedPromptId) {
      const matchingTemplate = sortedTemplates.find(
        (item) => item.prompt === editPromptValue && item.shortName === savedPromptName
      );

      if (matchingTemplate) {
        console.log(
          "🔄 UI: Restoring selected prompt from controller:",
          matchingTemplate.shortName
        );
        setSelectedPromptId(matchingTemplate.id);
        setIsSavedPromptSelected(true);
        sessionStorage.setItem("selectedPromptId", matchingTemplate.id);
      }
    }
    // Removed auto-selection of first prompt - let user choose from dropdown or type new

    isInitialRestore.current = false;
  }, [sortedTemplates, setEditPromptValue, setSavedPromptName]);

  const handlePromptSelect = (item: PromptHistoryItem) => {
    setEditPromptValue(item.prompt);
    setSavedPromptName(item.shortName);
    setSelectedPromptId(item.id);
    setIsSavedPromptSelected(true);
    setShowNameValidationError(false);
    setActiveTab("prompt");
    sessionStorage.setItem("selectedPromptId", item.id);
  };

  const handleOpenInsights = async () => {
    if (editPromptValue.length < 10) return;

    const isPromptEdited = editPromptValue.trim() !== DEFAULT_PROMPT.trim();
    if (!isSavedPromptSelected && isPromptEdited && !savedPromptName.trim()) {
      setTimeout(() => {
        setShowNameValidationError(true);
      }, 0);
      return;
    }

    setShowNameValidationError(false);

    await onOpenInsights(editPromptValue, savedPromptName.trim());
  };

  return (
    <>
      <style>
        {`
          .bg-gradient-custom {
            background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
          }
          /* Hide scrollbar for Chrome, Safari and Opera */
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          /* Hide scrollbar for IE, Edge and Firefox */
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          /* Responsive prompt name and save button layout */
          .prompt-save-container {
            display: flex;
            flex-direction: row;
            gap: 0.5rem;
          }
          @media (max-width: 380px) {
            .prompt-save-container {
              flex-direction: column;
            }
            .prompt-save-button {
              width: 100%;
            }
          }
        `}
      </style>
      <div className="h-screen flex flex-col bg-gradient-custom">
        <SpreetailHeader />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-3 pb-3 min-h-0">
          <div className="max-w-[600px] mx-auto pt-3">
            {/* Header */}
            <div className="mb-3">
              <h1 className="text-[#0F172A] text-lg font-semibold mb-1">Modify your prompt</h1>
              <p className="text-[#667085] text-xs font-normal">
                Strategic insights and recommendations based on your data
              </p>
            </div>

            {/* Tab Navigation */}
            <div className="flex bg-white/40 backdrop-blur-md rounded-xl p-1 mb-5 gap-1">
              <button
                onClick={() => setActiveTab("prompt")}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-all ${
                  activeTab === "prompt"
                    ? "bg-[#068E75] text-white rounded-lg shadow-sm"
                    : "bg-transparent text-[#64748B]"
                }`}
              >
                Prompt area
              </button>
              <button
                onClick={() => setActiveTab("history")}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-all ${
                  activeTab === "history"
                    ? "bg-[#068E75] text-white rounded-lg shadow-sm"
                    : "bg-transparent text-[#64748B]"
                }`}
              >
                History
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === "prompt" && (
              <div>
                {/* Use a saved prompt section */}
                <div className="mb-3">
                  <div className="mb-2">
                    <span className="text-[#0F172A] text-sm font-medium">Use a saved prompt</span>
                  </div>

                  <select
                    value={selectedPromptId || ""}
                    onChange={(e) => {
                      if (e.target.value === "") {
                        // User selected "Select used prompt" - clear everything
                        setSelectedPromptId(null);
                        setIsSavedPromptSelected(false);
                        setEditPromptValue("");
                        setSavedPromptName("");
                        sessionStorage.removeItem("selectedPromptId");
                      } else {
                        const selected = sortedTemplates.find((item) => item.id === e.target.value);
                        if (selected) {
                          handlePromptSelect(selected);
                          sessionStorage.setItem("selectedPromptId", selected.id);
                        }
                      }
                    }}
                    className="w-full bg-white/60 backdrop-blur-[10px] rounded-lg px-3 py-2.5 text-sm border border-[#E5E7EB] focus:border-[#068E75] focus:outline-none"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='18' height='18' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 8L10 12L14 8' stroke='%23667085' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 10px center",
                      paddingRight: "36px",
                      appearance: "none",
                      WebkitAppearance: "none",
                      MozAppearance: "none",
                    }}
                  >
                    <option value="">Select used prompt</option>
                    {sortedTemplates.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.shortName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Type your prompt section with Open Insights, Prompt name and Save prompt */}
                <div className="mb-3 bg-white/60 backdrop-blur-[10px] rounded-lg p-4 border border-white/60">
                  <div className="flex items-center gap-2 mb-2">
                    <img
                      src="/assets/Prompt.png"
                      alt="Prompt"
                      style={{ width: "20px", height: "20px", objectFit: "contain" }}
                    />
                    <span className="text-[#0F172A] text-sm font-medium">Type your prompt</span>
                  </div>

                  <textarea
                    className="w-full min-h-[80px] mt-2 bg-white/60 backdrop-blur-[10px] rounded-lg text-sm font-[inherit] border border-[#E5E7EB] focus:border-[#068E75] focus:outline-none p-3 resize-y hide-scrollbar"
                    rows={3}
                    value={editPromptValue}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setEditPromptValue(newValue);
                      setShowNameValidationError(false);
                      setSaveStatus({ type: null, message: "" });

                      if (selectedPromptId) {
                        const selectedPrompt = sortedTemplates.find(
                          (p) => p.id === selectedPromptId
                        );
                        if (selectedPrompt) {
                          if (newValue !== selectedPrompt.prompt) {
                            if (savedPromptName === selectedPrompt.shortName) {
                              setSavedPromptName("");
                            }
                            setSelectedPromptId(null);
                            setIsSavedPromptSelected(false);
                            sessionStorage.removeItem("selectedPromptId");
                          } else if (savedPromptName !== selectedPrompt.shortName) {
                            setSelectedPromptId(null);
                            setIsSavedPromptSelected(false);
                            sessionStorage.removeItem("selectedPromptId");
                          }
                        }
                      }
                    }}
                    placeholder="Enter your prompt here.."
                  />
                  {editPromptValue.length > 0 && editPromptValue.length < 10 && (
                    <span className="text-red-600 text-xs mt-1 block">
                      Minimum 10 characters required
                    </span>
                  )}

                  {/* Prompt name and Save prompt - In the same card */}
                  <div className="mt-3">
                    <div className="prompt-save-container">
                      <input
                        type="text"
                        className={`flex-1 bg-white/60 backdrop-blur-[10px] rounded-lg px-3 py-2.5 text-sm border ${
                          showNameValidationError ? "border-red-500" : "border-[#E5E7EB]"
                        } focus:border-[#068E75] focus:outline-none`}
                        value={savedPromptName}
                        onChange={(e) => {
                          const newName = e.target.value;
                          setSavedPromptName(newName);
                          setShowNameValidationError(false);
                          setSaveStatus({ type: null, message: "" });

                          if (selectedPromptId) {
                            const selectedPrompt = sortedTemplates.find(
                              (p) => p.id === selectedPromptId
                            );
                            if (
                              selectedPrompt &&
                              (editPromptValue !== selectedPrompt.prompt ||
                                newName !== selectedPrompt.shortName)
                            ) {
                              // User has edited, clear the selection to allow creating new prompt
                              setSelectedPromptId(null);
                              setIsSavedPromptSelected(false);
                              sessionStorage.removeItem("selectedPromptId");
                            }
                          }
                        }}
                        placeholder="Enter Prompt name"
                      />
                      <button
                        onClick={async () => {
                          if (!savedPromptName.trim()) {
                            setShowNameValidationError(true);
                            setSaveStatus({ type: null, message: "" });
                            return;
                          }
                          if (editPromptValue.length < 10) {
                            setSaveStatus({ type: null, message: "" });
                            return;
                          }

                          try {
                            setSaveStatus({ type: null, message: "" });
                            await onSavePrompt(editPromptValue, savedPromptName);
                            setSaveStatus({
                              type: "success",
                              message: "Prompt saved successfully!",
                            });
                            setTimeout(() => {
                              setSaveStatus({ type: null, message: "" });
                            }, 3000);
                          } catch (error) {
                            const errorMessage =
                              error instanceof Error
                                ? error.message
                                : "Failed to save prompt. Please try again.";
                            setSaveStatus({ type: "error", message: errorMessage });
                            setTimeout(() => {
                              setSaveStatus({ type: null, message: "" });
                            }, 4000);
                          }
                        }}
                        disabled={!savedPromptName.trim() || editPromptValue.length < 10}
                        className={`prompt-save-button px-5 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap border ${
                          !savedPromptName.trim() || editPromptValue.length < 10
                            ? "bg-white/30 border-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed opacity-60"
                            : "bg-white border-[#068E75] text-[#068E75] hover:bg-[#068E75] hover:text-white cursor-pointer"
                        }`}
                      >
                        Save prompt
                      </button>
                    </div>
                    {showNameValidationError && (
                      <span className="text-red-600 text-xs mt-1 block">
                        Please provide a name for your custom prompt
                      </span>
                    )}
                    {saveStatus.type && (
                      <div
                        className={`mt-2 px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-2 ${
                          saveStatus.type === "success"
                            ? "bg-[#D4EDDA] text-[#155724] border border-[#C3E6CB]"
                            : "bg-[#FFF3CD] text-[#856404] border border-[#FFC107]"
                        }`}
                      >
                        {saveStatus.type === "success" ? (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                        {saveStatus.message}
                      </div>
                    )}
                  </div>

                  {/* Open Insights Button */}
                  <button
                    onClick={handleOpenInsights}
                    disabled={!editPromptValue.trim() || editPromptValue.length < 10}
                    className={`w-full mt-3 py-2.5 px-4 rounded-lg text-white text-sm font-semibold transition-all ${
                      !editPromptValue.trim() || editPromptValue.length < 10
                        ? "bg-gray-400 cursor-not-allowed opacity-60"
                        : "bg-[#068E75] hover:bg-[#057A63] cursor-pointer"
                    }`}
                  >
                    Open Insights
                  </button>
                </div>
              </div>
            )}

            {activeTab === "history" && (
              <div>
                {/* Header */}
                <div className="mb-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-[#0F172A] text-base font-semibold">History</h2>
                  </div>
                </div>

                {/* History list */}
                {sortedHistory.length === 0 ? (
                  <div className="bg-white/60 backdrop-blur-[10px] rounded-lg p-5 text-center border border-white/60">
                    <p className="text-[#667085] text-sm">
                      No saved history yet. Your recent history will appear here.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2.5">
                      {visibleHistory.map((item) => (
                        <div
                          key={item.id}
                          className="bg-white/60 backdrop-blur-[10px] rounded-lg p-3.5 border border-white/60 cursor-pointer hover:border-[#068E75] transition-all"
                          onClick={() => history.push(`/ai-insights?run_id=${item.id}`)}
                        >
                          <div className="flex items-start justify-between mb-1.5">
                            <h3 className="text-[#0F172A] text-sm font-semibold">{item.shortName}</h3>
                            <span className="text-[#667085] text-xs">
                              {new Date(item.createdAt).toLocaleDateString("en-US", {
                                month: "2-digit",
                                day: "2-digit",
                                year: "numeric",
                              })}{" "}
                              |{" "}
                              {new Date(item.createdAt).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </span>
                          </div>
                          <p className="text-[#667085] text-xs line-clamp-2">{item.prompt}</p>
                        </div>
                      ))}
                    </div>
                    {totalPages > 1 && (
                      <div className="mt-4 flex items-center justify-between">
                        <button
                          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                          disabled={!canGoPrevious}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            canGoPrevious
                              ? "bg-white/60 backdrop-blur-[10px] border border-white/60 hover:border-[#068E75] text-[#068E75] cursor-pointer"
                              : "bg-white/30 backdrop-blur-[10px] border border-white/30 text-[#667085] cursor-not-allowed opacity-50"
                          }`}
                        >
                          Previous
                        </button>
                        <span className="text-[#667085] text-sm">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                          disabled={!canGoNext}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            canGoNext
                              ? "bg-white/60 backdrop-blur-[10px] border border-white/60 hover:border-[#068E75] text-[#068E75] cursor-pointer"
                              : "bg-white/30 backdrop-blur-[10px] border border-white/30 text-[#667085] cursor-not-allowed opacity-50"
                          }`}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};